import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import {
  departmentsService,
  Department,
  CreateDepartmentDto,
  UpdateDepartmentDto,
} from '@/services/departments.service';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Building2,
  Users,
  MessageSquare,
  Star,
  Check,
  X,
} from 'lucide-react';
import { cn } from '@caruso/ui';

export function DepartmentsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateDepartmentDto>({ name: '', description: '' });

  const { data: departments, isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateDepartmentDto) => departmentsService.create(data),
    onSuccess: () => {
      toast({
        title: 'Departamento criado',
        description: 'Departamento criado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setIsCreating(false);
      setFormData({ name: '', description: '' });
    },
    onError: () => {
      toast({
        title: 'Erro ao criar',
        description: 'Não foi possível criar o departamento',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDepartmentDto }) =>
      departmentsService.update(id, data),
    onSuccess: () => {
      toast({
        title: 'Departamento atualizado',
        description: 'Departamento atualizado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setEditingId(null);
      setFormData({ name: '', description: '' });
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar',
        description: 'Não foi possível atualizar o departamento',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => departmentsService.delete(id),
    onSuccess: () => {
      toast({
        title: 'Departamento excluído',
        description: 'Departamento removido com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir',
        description: 'Não foi possível excluir o departamento',
        variant: 'destructive',
      });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => departmentsService.setAsDefault(id),
    onSuccess: () => {
      toast({
        title: 'Departamento padrão',
        description: 'Departamento definido como padrão',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Não foi possível definir como padrão',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Tem certeza que deseja excluir o departamento "${name}"?`)) return;
    deleteMutation.mutate(id);
  };

  const handleCreate = () => {
    if (!formData.name.trim()) return;
    createMutation.mutate(formData);
  };

  const handleUpdate = (id: string) => {
    if (!formData.name.trim()) return;
    updateMutation.mutate({ id, data: formData });
  };

  const startEditing = (dept: Department) => {
    setEditingId(dept.id);
    setFormData({ name: dept.name, description: dept.description || '' });
    setIsCreating(false);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setFormData({ name: '', description: '' });
  };

  const filteredDepartments = departments?.filter((dept) => {
    const searchLower = search.toLowerCase();
    return (
      dept.name.toLowerCase().includes(searchLower) ||
      dept.description?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-brand-smoke rounded w-48"></div>
          <div className="h-32 bg-brand-smoke rounded"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-brand-smoke rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white">Departamentos</h1>
          <p className="text-brand-ivory/80 mt-2 text-lg">
            Gerencie os departamentos de atendimento
          </p>
        </div>
        <Button
          onClick={() => {
            setIsCreating(true);
            setEditingId(null);
            setFormData({ name: '', description: '' });
          }}
          className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90 font-semibold shadow-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Departamento
        </Button>
      </div>

      {/* Search */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
          <Input
            type="text"
            placeholder="Buscar por nome ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 text-base"
          />
        </div>
      </Card>

      {/* Create Form */}
      {isCreating && (
        <Card className="p-6 bg-brand-smoke border-brand-gold/40">
          <h3 className="text-lg font-semibold text-white mb-4">Novo Departamento</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-ivory/80 mb-1">Nome</label>
              <Input
                type="text"
                placeholder="Nome do departamento"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-ivory/80 mb-1">
                Descrição (opcional)
              </label>
              <Input
                type="text"
                placeholder="Descrição do departamento"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending || !formData.name.trim()}
                className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
              >
                <Check className="w-4 h-4 mr-2" />
                Criar
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setFormData({ name: '', description: '' });
                }}
                className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Departments List */}
      <div className="space-y-4">
        {filteredDepartments && filteredDepartments.length > 0 ? (
          filteredDepartments.map((dept) => (
            <motion.div
              key={dept.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="p-6 bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                {editingId === dept.id ? (
                  // Edit Form
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-1">
                        Nome
                      </label>
                      <Input
                        type="text"
                        placeholder="Nome do departamento"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-1">
                        Descrição (opcional)
                      </label>
                      <Input
                        type="text"
                        placeholder="Descrição do departamento"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleUpdate(dept.id)}
                        disabled={updateMutation.isPending || !formData.name.trim()}
                        className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Salvar
                      </Button>
                      <Button
                        variant="outline"
                        onClick={cancelEditing}
                        className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Department Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-gold to-yellow-600 flex items-center justify-center shadow-lg">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-xl font-bold text-white truncate">{dept.name}</h3>
                            {dept.isDefault && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                                <Star className="w-3 h-3" />
                                Padrão
                              </span>
                            )}
                          </div>
                          {dept.description && (
                            <p className="text-base text-brand-ivory/70 truncate">
                              {dept.description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 mt-4">
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                          <Users className="w-3 h-3" />
                          {dept._count?.users || 0} usuários
                        </span>

                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          <MessageSquare className="w-3 h-3" />
                          {dept._count?.conversations || 0} conversas
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/app/departamentos/${dept.id}/usuarios`)}
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Usuários
                      </Button>

                      {!dept.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDefaultMutation.mutate(dept.id)}
                          disabled={setDefaultMutation.isPending}
                          className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Definir Padrão
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing(dept)}
                        className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(dept.id, dept.name)}
                        disabled={deleteMutation.isPending}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </motion.div>
          ))
        ) : (
          <Card className="p-12 bg-brand-smoke border-brand-gold/20">
            <div className="text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-brand-gold/30" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {search ? 'Nenhum departamento encontrado' : 'Nenhum departamento cadastrado'}
              </h3>
              <p className="text-brand-ivory/60 mb-6">
                {search
                  ? 'Tente ajustar os termos da busca'
                  : 'Comece adicionando seu primeiro departamento'}
              </p>
              {!search && (
                <Button
                  onClick={() => {
                    setIsCreating(true);
                    setFormData({ name: '', description: '' });
                  }}
                  className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Adicionar Departamento
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
